import { Dispatch, SetStateAction } from "react";
import { createAndDownloadInvoicePdf } from "../../../features/viewOrder/ViewOrderUtils";
import { getAccessToken } from "../../../services/authenticationService";
import { getConfigData } from "../../../services/configurationService";
import {
  getActiveUsersBySupplierAndDesignation,
  getRouteInfoList,
  getVehicleMaster,
  getSalesDataByDeliveryDate,
  getOrderListByDeliveryDate,
  getSalesDataListByDeliveryDate,
  getLoadoutExcelSheet,
} from "../../../services/loadoutService";
import { openPopup } from "../../../stateManagement/actions/popupActions";
import {
  ActionDisplayPreference,
  AllowedStatuses,
  ConfigState,
  DataDisplayPreference,
  InvoicesCount,
  loadoutsSalesData,
  Product,
  VehicleOption,
} from "../common/types";
import {
  initalFiltersConfig,
  presellerColumnsMetaData,
} from "./PresellerUtils";
import dayjs, { Dayjs } from "dayjs";
import { AnyAction } from "redux";
import { safeStringify, truncateToTwoDecimals } from "../../dms_common/DmsUtils";
import { loadOutColumnsMetaData } from "../LoadoutUtils";
import { logAnalyticsEvent, logLoadTrace } from "../../../firebase";
import { downloadSingleReport } from "../../../services/manageReportService";
import { getDownloadSingleReportConfig as getLoadoutDownloadSingleReportConfig } from "../LoadoutUtils";
import { DownloadSingleReportConfig, popupType } from "../../../types/types";
import { getOutletDetails as getOutletDetailsFromIndexDb } from "../../../features/createSecondaryOrder/CreateSecondaryOrderService";
import { getDataFromLocalDb } from "../../../services/dbService";
import { sendSlackAlert, SlackChannel } from "../../../services/slackService";
import { getDayEndStatusFromSession } from "../../dms_common/DayEndUtils";

const getGroupingKey = (
  order: any,
  batchIdConfiguration: boolean,
  batchId: string
): string => {
  if (batchIdConfiguration) {
    return `${order.skuCode}-${order.batchCode}-${order.type}-${batchId}`;
  } else {
    return `${order.batchCode}-${order.type}`;
  }
};

const batchArray = <T>(array: T[], batchSize: number): T[][] => {
  const batches: T[][] = [];
  for (let i = 0; i < array.length; i += batchSize) {
    batches.push(array.slice(i, i + batchSize));
  }
  return batches;
};

const updateQuantityOrderWise = (
  order: any,
  updatedProduct: { [key: string]: Product },
  switchState: boolean,
  products: any,
  isAdding: boolean,
  totals: {
    totalAmount: number;
    totalCase: number;
    totalPiece: number;
    totalOther: number;
  },
  setSnackbarMessage: Dispatch<SetStateAction<string>>,
  setSnackbarOpen: Dispatch<SetStateAction<boolean>>,
  performVehiclecalculation: boolean | undefined,
  vehicleUOMKeys: any,
  updatedVehicleCapacity: { cubicFeet: number; KG: number },
  displayVehicleCapacityValidationErrorMessage: boolean,
  batchIdConfiguration: boolean
) => {
  const batchId = order.batchId ?? order.batchIds?.[0]?.batchId ?? "N/A";
  const groupingKey = getGroupingKey(order, batchIdConfiguration, batchId);
  const caseToPiece = products?.[order.batchCode]?.caseToPieceQuantity || 1;
  const caseToOther = products?.[order.batchCode]?.caseToOtherUnitQuantity || 1;
  const otherToPiece =
    products?.[order.batchCode]?.otherUnitToPieceQuantity || 1;

  if (isAdding) {
    logAnalyticsEvent("delivery_loadout_preseller_order_selected", {
      distributor_id: JSON.parse(localStorage.authContext).user.loginId,
      preseller_loadout_order_selected: order?.orderNumber,
    });

    if (updatedProduct[groupingKey]) {
      let newPieceQuantity =
        updatedProduct[groupingKey].pieceQuantity + order.pieceQuantity;
      let newCaseQuantity =
        updatedProduct[groupingKey].caseQuantity + order.caseQuantity;
      let newOtherQuantity =
        updatedProduct[groupingKey].otherUnitQuantity + order.otherUnitQuantity;

      if (performVehiclecalculation) {
        const multiplicationFactorForVolume =
          order.initialCaseQuantity +
          order.initialPieceQuantity / caseToPiece +
          order.otherUnitQuantity / caseToOther;

        const totalCaseWeight = calculateTotalCasesWeight(
          order.caseQuantity,
          updatedProduct[groupingKey],
          caseToPiece,
          caseToOther,
          vehicleUOMKeys?.[updatedProduct[groupingKey]?.skuCsWeightUOM ?? ""] ||
            1,
          setSnackbarMessage,
          setSnackbarOpen,
          displayVehicleCapacityValidationErrorMessage
        );

        const totalPieceVWeight = calculateTotalPiecesWeight(
          order.pieceQuantity,
          updatedProduct[groupingKey],
          caseToPiece,
          otherToPiece,
          vehicleUOMKeys?.[updatedProduct[groupingKey]?.skuPcWeightUOM ?? ""] ||
            1,
          setSnackbarMessage,
          setSnackbarOpen,
          displayVehicleCapacityValidationErrorMessage
        );

        const totalOtherVWeight = calculateTotalOtherWeight(
          order.otherUnitQuantity,
          updatedProduct[groupingKey],
          caseToOther,
          otherToPiece,
          vehicleUOMKeys?.[
            updatedProduct[groupingKey]?.skuOterhWeightUOM ?? ""
          ] || 1,
          setSnackbarMessage,
          setSnackbarOpen,
          displayVehicleCapacityValidationErrorMessage
        );

        const totalSkuWeight =
          totalCaseWeight + totalPieceVWeight + totalOtherVWeight;

        handleVehicleCapacity(
          updatedProduct[groupingKey],
          setSnackbarMessage,
          setSnackbarOpen,
          false,
          multiplicationFactorForVolume,
          totalSkuWeight,
          updatedVehicleCapacity,
          displayVehicleCapacityValidationErrorMessage
        );
      }

      updatedProduct[groupingKey] = {
        ...updatedProduct[groupingKey],
        caseQuantity: newCaseQuantity,
        pieceQuantity: newPieceQuantity,
        otherUnitQuantity: newOtherQuantity,
      };

      totals.totalCase += order.caseQuantity || 0;
      totals.totalPiece += order.pieceQuantity;
      totals.totalOther += order.otherUnitQuantity;
    } else {
      let { pieceQuantity, caseQuantity, otherUnitQuantity } = order;

      const product: Product = {
        id: order.id,
        lastModified: new Date(order?.lastModifiedTime),
        batchCode: order.batchCode,
        skuCode: order.skuCode,
        imageUrl: products?.[order.batchCode]?.blobKey,
        otherUnitName: products?.[order.batchCode]?.otherUnitName,
        name: products?.[order.batchCode]?.skuDescription,
        itemType: order.type,
        pieceQuantity,
        caseQuantity,
        otherUnitQuantity,
        qtyUnit: order.extendedAttributes?.qtyUnit,
        amount: order?.extendedAttributes?.packPtr,
        price: order.packMrp,
        margin: order.discountMargin,
        batchId: batchId,
        netPtr: order?.extendedAttributes?.packPtr,
        maxDiscount: 0,
        volume: 0,
        skuCaseBase: products?.[order.batchCode]?.extendedAttributes?.skuCaseB,
        skuCaseHeight:
          products?.[order.batchCode]?.extendedAttributes?.skuCaseH,
        skuCaseLength:
          products?.[order.batchCode]?.extendedAttributes?.skuCaseL,
        skuCaseVolume:
          products?.[order.batchCode]?.extendedAttributes?.skuCaseVolume,
        skuCaseWeight:
          products?.[order.batchCode]?.extendedAttributes?.skuCaseWeight,
        skuCsWeightUOM:
          products?.[order.batchCode]?.extendedAttributes?.skuCsWeightUOM,
        skuPcWeightUOM:
          products?.[order.batchCode]?.extendedAttributes?.skuPcWeightUOM,
        skuPieceWeight:
          products?.[order.batchCode]?.extendedAttributes?.skuPieceWeight,
        skuOtherWeight:
          products?.[order.batchCode]?.extendedAttributes?.skuOtherWeight,
        skuOterhWeightUOM:
          products?.[order.batchCode]?.extendedAttributes?.skuOterhWeightUOM,
        savings: 0,
        skuDesc: order.name,
        visitedOffers: [],
        supplier: order.supplier,
        itemDescV1: order.itemDescV1,
        stockQtyV1: order.stockQty,
        typeV1: order.typeV1,
        buttonDescriptionV1: order.buttonDescriptionV1,
        creditQty: 0,
        ssp: order.srp,
        discountMargin: order.discountMargin,
        sbu: order?.extendedAttributes?.sbu,
        packMrp: order.packMrp,
        casePrice: order.casePrice,
        mrp: order?.extendedAttributes?.mrp,
        caseMrp: order.caseMrp,
        case2Piece: products?.[order.batchCode]?.caseToPieceQuantity,
        otherUnit2Piece: products?.[order.batchCode]?.caseToOtherUnitQuantity,
        pieceSize: products?.[order.batchCode]?.pieceSize,
        otherUnitPrice: order.otherUnitPrice,
        availableCaseQty: order.caseQuantity,
        availablePieceQty: order.pieceQuantity,
        availableOtherQty: order.otherUnitQuantity,
        casePtr: order?.casePrice,
        packPtr: switchState
          ? order?.price
          : order?.extendedAttributes?.packPtr,
      };

      if (performVehiclecalculation) {
        const multiplicationFactorForVolume =
          caseQuantity +
          pieceQuantity / caseToPiece +
          otherUnitQuantity / caseToOther;

        const totalCaseWeight = calculateTotalCasesWeight(
          caseQuantity,
          product,
          caseToPiece,
          caseToOther,
          vehicleUOMKeys?.[product?.skuCsWeightUOM ?? ""] || 1,
          setSnackbarMessage,
          setSnackbarOpen,
          displayVehicleCapacityValidationErrorMessage
        );

        const totalPieceVWeight = calculateTotalPiecesWeight(
          pieceQuantity,
          product,
          caseToPiece,
          otherToPiece,
          vehicleUOMKeys?.[product?.skuPcWeightUOM ?? ""] || 1,
          setSnackbarMessage,
          setSnackbarOpen,
          displayVehicleCapacityValidationErrorMessage
        );

        const totalOtherVWeight = calculateTotalOtherWeight(
          otherUnitQuantity,
          product,
          caseToOther,
          otherToPiece,
          vehicleUOMKeys?.[product?.skuOterhWeightUOM ?? ""] || 1,
          setSnackbarMessage,
          setSnackbarOpen,
          displayVehicleCapacityValidationErrorMessage
        );

        const totalSkuWeight =
          totalCaseWeight + totalPieceVWeight + totalOtherVWeight;

        handleVehicleCapacity(
          product,
          setSnackbarMessage,
          setSnackbarOpen,
          false,
          multiplicationFactorForVolume,
          totalSkuWeight,
          updatedVehicleCapacity,
          displayVehicleCapacityValidationErrorMessage
        );
      }

      if (product) {
        updatedProduct[groupingKey] = {
          ...product,
        };
      }

      totals.totalCase += caseQuantity || 0;
      totals.totalPiece += pieceQuantity || 0;
      totals.totalOther += otherUnitQuantity || 0;
    }
  } else if (updatedProduct[groupingKey]) {
    logAnalyticsEvent("delivery_loadout_preseller_order_deselected", {
      distributor_id: JSON.parse(localStorage.authContext).user.loginId,
      preseller_loadout_order_deselected: order?.orderNumber,
    });

    let { pieceQuantity, caseQuantity, otherUnitQuantity } = order;

    let newPieceQuantity =
      updatedProduct[groupingKey].pieceQuantity - pieceQuantity;
    let newCaseQuantity =
      updatedProduct[groupingKey].caseQuantity - caseQuantity;
    let newOtherQuantity =
      updatedProduct[groupingKey].otherUnitQuantity - otherUnitQuantity;

    updatedProduct[groupingKey] = {
      ...updatedProduct[groupingKey],
      caseQuantity: newCaseQuantity,
      pieceQuantity: newPieceQuantity,
      otherUnitQuantity: newOtherQuantity,
    };

    if (performVehiclecalculation) {
      const multiplicationFactorForVolume =
        caseQuantity +
        pieceQuantity / caseToPiece +
        otherUnitQuantity / caseToOther;

      const totalCaseWeight = calculateTotalCasesWeight(
        caseQuantity,
        updatedProduct[groupingKey],
        caseToPiece,
        caseToOther,
        vehicleUOMKeys?.[updatedProduct[groupingKey]?.skuCsWeightUOM ?? ""] ||
          1,
        setSnackbarMessage,
        setSnackbarOpen,
        displayVehicleCapacityValidationErrorMessage
      );

      const totalPieceVWeight = calculateTotalPiecesWeight(
        pieceQuantity,
        updatedProduct[groupingKey],
        caseToPiece,
        otherToPiece,
        vehicleUOMKeys?.[updatedProduct[groupingKey]?.skuPcWeightUOM ?? ""] ||
          1,
        setSnackbarMessage,
        setSnackbarOpen,
        displayVehicleCapacityValidationErrorMessage
      );

      const totalOtherVWeight = calculateTotalOtherWeight(
        otherUnitQuantity,
        updatedProduct[groupingKey],
        caseToOther,
        otherToPiece,
        vehicleUOMKeys?.[
          updatedProduct[groupingKey]?.skuOterhWeightUOM ?? ""
        ] || 1,
        setSnackbarMessage,
        setSnackbarOpen,
        displayVehicleCapacityValidationErrorMessage
      );

      const totalSkuWeight =
        totalCaseWeight + totalPieceVWeight + totalOtherVWeight;

      handleVehicleCapacity(
        updatedProduct[groupingKey],
        setSnackbarMessage,
        setSnackbarOpen,
        true,
        multiplicationFactorForVolume,
        totalSkuWeight,
        updatedVehicleCapacity,
        displayVehicleCapacityValidationErrorMessage
      );
    }

    if (
      updatedProduct[groupingKey].caseQuantity <= 0 &&
      updatedProduct[groupingKey].otherUnitQuantity <= 0 &&
      updatedProduct[groupingKey].pieceQuantity <= 0
    ) {
      delete updatedProduct[groupingKey];
    }

    totals.totalCase -= caseQuantity || 0;
    totals.totalPiece -= pieceQuantity || 0;
    totals.totalOther -= otherUnitQuantity || 0;
  }
};

const updateQuantityInvoiceWise = (
  order: any,
  updatedProduct: { [key: string]: Product },
  products: any,
  isAdding: boolean,
  totals: {
    totalAmount: number;
    totalCase: number;
    totalPiece: number;
    totalOther: number;
  },
  setSnackbarMessage: Dispatch<SetStateAction<string>>,
  setSnackbarOpen: Dispatch<SetStateAction<boolean>>,
  performVehiclecalculation: boolean | undefined,
  vehicleUOMKeys: any,
  updatedVehicleCapacity: { cubicFeet: number; KG: number },
  wasVehicleJustAssigned: boolean,
  displayVehicleCapacityValidationErrorMessage: boolean,
  batchIdConfiguration: boolean,
  isViewLoadout: boolean,
  previousCheckedIdsSize: number
) => {
  const batchId = order.batchId ?? order.batchIds?.[0]?.batchId ?? "N/A";
  const groupingKey = getGroupingKey(order, batchIdConfiguration, batchId);

  const caseToPiece = products?.[order.batchCode]?.caseToPieceQuantity || 1;
  const caseToOther = products?.[order.batchCode]?.caseToOtherUnitQuantity || 1;
  const otherToPiece =
    products?.[order.batchCode]?.otherUnitToPieceQuantity || 1;

  if (isAdding) {
    logAnalyticsEvent("delivery_loadout_preseller_invoice_selected", {
      distributor_id: JSON.parse(localStorage.authContext).user.loginId,
      preseller_loadout_invoice_selected: order?.invoiceNumber,
    });

    if (updatedProduct[groupingKey]) {
      let newPieceQuantity =
        updatedProduct[groupingKey].pieceQuantity + order.initialPieceQuantity;
      let newCaseQuantity =
        updatedProduct[groupingKey].caseQuantity + order.initialCaseQuantity;
      let newOtherQuantity =
        updatedProduct[groupingKey].otherUnitQuantity +
        order.initialOtherUnitQuantity;

      if (performVehiclecalculation) {
        const multiplicationFactorForVolume =
          order.initialCaseQuantity +
          order.initialPieceQuantity / caseToPiece +
          order.initialOtherUnitQuantity / caseToOther;

        const totalCaseWeight = calculateTotalCasesWeight(
          order.initialCaseQuantity,
          updatedProduct[groupingKey],
          caseToPiece,
          caseToOther,
          vehicleUOMKeys?.[updatedProduct[groupingKey]?.skuCsWeightUOM ?? ""] ||
            1,
          setSnackbarMessage,
          setSnackbarOpen,
          displayVehicleCapacityValidationErrorMessage
        );

        const totalPieceVWeight = calculateTotalPiecesWeight(
          order.initialPieceQuantity,
          updatedProduct[groupingKey],
          caseToPiece,
          otherToPiece,
          vehicleUOMKeys?.[updatedProduct[groupingKey]?.skuPcWeightUOM ?? ""] ||
            1,
          setSnackbarMessage,
          setSnackbarOpen,
          displayVehicleCapacityValidationErrorMessage
        );

        const totalOtherVWeight = calculateTotalOtherWeight(
          order.initialOtherUnitQuantity,
          updatedProduct[groupingKey],
          caseToOther,
          otherToPiece,
          vehicleUOMKeys?.[
            updatedProduct[groupingKey]?.skuOterhWeightUOM ?? ""
          ] || 1,
          setSnackbarMessage,
          setSnackbarOpen,
          displayVehicleCapacityValidationErrorMessage
        );

        const totalSkuWeight =
          totalCaseWeight + totalPieceVWeight + totalOtherVWeight;

        handleVehicleCapacity(
          updatedProduct[groupingKey],
          setSnackbarMessage,
          setSnackbarOpen,
          false,
          multiplicationFactorForVolume,
          totalSkuWeight,
          updatedVehicleCapacity,
          displayVehicleCapacityValidationErrorMessage
        );
      }

      updatedProduct[groupingKey] = {
        ...updatedProduct[groupingKey],
        caseQuantity: newCaseQuantity,
        pieceQuantity: newPieceQuantity,
        otherUnitQuantity: newOtherQuantity,
      };

      const shouldUpdateTotals = isViewLoadout
        ? true
        : !wasVehicleJustAssigned || previousCheckedIdsSize === 0;

      if (shouldUpdateTotals) {
        totals.totalCase += order.initialCaseQuantity;
        totals.totalPiece += order.initialPieceQuantity;
        totals.totalOther += order.initialOtherUnitQuantity;
      }
    } else {
      let {
        initialPieceQuantity,
        initialCaseQuantity,
        initialOtherUnitQuantity,
      } = order;

      const product: Product = {
        id: order.id,
        lastModified: new Date(order?.lastModifiedTime),
        batchCode: order.batchCode,
        skuCode: order.skuCode,
        imageUrl: products?.[order.batchCode]?.blobKey,
        otherUnitName: products?.[order.batchCode]?.otherUnitName,
        name: products?.[order.batchCode]?.skuDescription,
        itemType: order.type,
        pieceQuantity: order?.initialPieceQuantity,
        caseQuantity: order?.initialCaseQuantity,
        otherUnitQuantity: order?.initialOtherUnitQuantity,
        qtyUnit: order.extendedAttributes?.qtyUnit,
        amount: order?.extendedAttributes?.packPtr,
        price: order.packMrp,
        margin: order.discountMargin,
        batchId: batchId,
        netPtr: order?.extendedAttributes?.packPtr,
        maxDiscount: 0,
        volume: 0,
        skuCaseBase: products?.[order.batchCode]?.extendedAttributes?.skuCaseB,
        skuCaseHeight:
          products?.[order.batchCode]?.extendedAttributes?.skuCaseH,
        skuCaseLength:
          products?.[order.batchCode]?.extendedAttributes?.skuCaseL,
        skuCaseVolume:
          products?.[order.batchCode]?.extendedAttributes?.skuCaseVolume,
        skuCaseWeight:
          products?.[order.batchCode]?.extendedAttributes?.skuCaseWeight,
        skuCsWeightUOM:
          products?.[order.batchCode]?.extendedAttributes?.skuCsWeightUOM,
        skuPcWeightUOM:
          products?.[order.batchCode]?.extendedAttributes?.skuPcWeightUOM,
        skuPieceWeight:
          products?.[order.batchCode]?.extendedAttributes?.skuPieceWeight,
        skuOtherWeight:
          products?.[order.batchCode]?.extendedAttributes?.skuOtherWeight,
        skuOterhWeightUOM:
          products?.[order.batchCode]?.extendedAttributes?.skuOterhWeightUOM,
        savings: 0,
        skuDesc: order.name,
        visitedOffers: [],
        supplier: order.supplier,
        itemDescV1: order.itemDescV1,
        stockQtyV1: order.stockQty,
        typeV1: order.typeV1,
        buttonDescriptionV1: order.buttonDescriptionV1,
        creditQty: 0,
        ssp: order.srp,
        discountMargin: order.discountMargin,
        sbu: order?.extendedAttributes?.sbu,
        packMrp: order.packMrp,
        mrp: order?.extendedAttributes?.mrp,
        caseMrp: order.caseMrp,
        case2Piece: products?.[order.batchCode]?.caseToPieceQuantity,
        otherUnit2Piece: products?.[order.batchCode]?.caseToOtherUnitQuantity,
        pieceSize: products?.[order.batchCode]?.pieceSize,
        otherUnitPrice: order.otherUnitPrice ?? 0,
        casePrice: order.casePrice,
        availableCaseQty: order.initialCaseQuantity,
        availablePieceQty: order.initialPieceQuantity,
        availableOtherQty: order.initialOtherUnitQuantity,
        casePtr: order?.casePrice ?? 0,
        packPtr: order?.price ?? 0,
      };

      if (performVehiclecalculation) {
        const multiplicationFactorForVolume =
          initialCaseQuantity +
          initialPieceQuantity / caseToPiece +
          initialOtherUnitQuantity / caseToOther;

        const totalCaseWeight = calculateTotalCasesWeight(
          initialCaseQuantity,
          product,
          caseToPiece,
          caseToOther,
          vehicleUOMKeys?.[product?.skuCsWeightUOM ?? ""] || 1,
          setSnackbarMessage,
          setSnackbarOpen,
          displayVehicleCapacityValidationErrorMessage
        );

        const totalPieceVWeight = calculateTotalPiecesWeight(
          initialPieceQuantity,
          product,
          caseToPiece,
          otherToPiece,
          vehicleUOMKeys?.[product?.skuPcWeightUOM ?? ""] || 1,
          setSnackbarMessage,
          setSnackbarOpen,
          displayVehicleCapacityValidationErrorMessage
        );

        const totalOtherVWeight = calculateTotalOtherWeight(
          initialOtherUnitQuantity,
          product,
          caseToOther,
          otherToPiece,
          vehicleUOMKeys?.[product?.skuOterhWeightUOM ?? ""] || 1,
          setSnackbarMessage,
          setSnackbarOpen,
          displayVehicleCapacityValidationErrorMessage
        );

        const totalSkuWeight =
          totalCaseWeight + totalPieceVWeight + totalOtherVWeight;

        handleVehicleCapacity(
          product,
          setSnackbarMessage,
          setSnackbarOpen,
          false,
          multiplicationFactorForVolume,
          totalSkuWeight,
          updatedVehicleCapacity,
          displayVehicleCapacityValidationErrorMessage
        );
      }

      if (product) {
        updatedProduct[groupingKey] = {
          ...product,
        };
      }

      const shouldUpdateTotals = isViewLoadout
        ? true
        : !wasVehicleJustAssigned || previousCheckedIdsSize === 0;

      if (shouldUpdateTotals) {
        totals.totalCase += initialCaseQuantity || 0;
        totals.totalPiece += initialPieceQuantity || 0;
        totals.totalOther += initialOtherUnitQuantity || 0;
      }
    }
  } else if (updatedProduct[groupingKey]) {
    logAnalyticsEvent("delivery_loadout_preseller_invoice_deselected", {
      distributor_id: JSON.parse(localStorage.authContext).user.loginId,
      preseller_loadout_invoice_deselected: order?.invoiceNumber,
    });

    let {
      initialPieceQuantity,
      initialCaseQuantity,
      initialOtherUnitQuantity,
    } = order;

    let newPieceQuantity =
      updatedProduct[groupingKey].pieceQuantity - initialPieceQuantity;
    let newCaseQuantity =
      updatedProduct[groupingKey].caseQuantity - initialCaseQuantity;
    let newOtherQuantity =
      updatedProduct[groupingKey].otherUnitQuantity - initialOtherUnitQuantity;

    updatedProduct[groupingKey] = {
      ...updatedProduct[groupingKey],
      caseQuantity: newCaseQuantity,
      pieceQuantity: newPieceQuantity,
      otherUnitQuantity: newOtherQuantity,
    };

    if (performVehiclecalculation) {
      const multiplicationFactorForVolume =
        initialCaseQuantity +
        initialPieceQuantity / caseToPiece +
        initialOtherUnitQuantity / caseToOther;

      const totalCaseWeight = calculateTotalCasesWeight(
        initialCaseQuantity,
        updatedProduct[groupingKey],
        caseToPiece,
        caseToOther,
        vehicleUOMKeys?.[updatedProduct[groupingKey]?.skuCsWeightUOM ?? ""] ||
          1,
        setSnackbarMessage,
        setSnackbarOpen,
        displayVehicleCapacityValidationErrorMessage
      );

      const totalPieceVWeight = calculateTotalPiecesWeight(
        initialPieceQuantity,
        updatedProduct[groupingKey],
        caseToPiece,
        otherToPiece,
        vehicleUOMKeys?.[updatedProduct[groupingKey]?.skuPcWeightUOM ?? ""] ||
          1,
        setSnackbarMessage,
        setSnackbarOpen,
        displayVehicleCapacityValidationErrorMessage
      );

      const totalOtherVWeight = calculateTotalOtherWeight(
        initialOtherUnitQuantity,
        updatedProduct[groupingKey],
        caseToOther,
        otherToPiece,
        vehicleUOMKeys?.[
          updatedProduct[groupingKey]?.skuOterhWeightUOM ?? ""
        ] || 1,
        setSnackbarMessage,
        setSnackbarOpen,
        displayVehicleCapacityValidationErrorMessage
      );

      const totalSkuWeight =
        totalCaseWeight + totalPieceVWeight + totalOtherVWeight;

      handleVehicleCapacity(
        updatedProduct[groupingKey],
        setSnackbarMessage,
        setSnackbarOpen,
        true,
        multiplicationFactorForVolume,
        totalSkuWeight,
        updatedVehicleCapacity,
        displayVehicleCapacityValidationErrorMessage
      );
    }

    if (
      updatedProduct[groupingKey].caseQuantity <= 0 &&
      updatedProduct[groupingKey].otherUnitQuantity <= 0 &&
      updatedProduct[groupingKey].pieceQuantity <= 0
    ) {
      delete updatedProduct[groupingKey];
    }

    totals.totalCase -= initialCaseQuantity || 0;
    totals.totalPiece -= initialPieceQuantity || 0;
    totals.totalOther -= initialOtherUnitQuantity || 0;
  }
};

const calculateTotalCasesWeight = (
  initialCaseQuantity: number,
  product: Product,
  caseToPiece: number,
  caseToOther: number,
  caseMultiplicationFactor: number,
  setSnackbarMessage: Dispatch<SetStateAction<string>>,
  setSnackbarOpen: Dispatch<SetStateAction<boolean>>,
  displayVehicleCapacityValidationErrorMessage: boolean
): number => {
  if (initialCaseQuantity > 0) {
    if (product.skuCaseWeight) {
      return (
        initialCaseQuantity * product.skuCaseWeight * caseMultiplicationFactor
      );
    } else if (product.skuPieceWeight) {
      return (
        initialCaseQuantity *
        (product.skuPieceWeight * caseToPiece) *
        caseMultiplicationFactor
      );
    } else if (product.skuOtherWeight) {
      return (
        initialCaseQuantity *
        (product.skuOtherWeight * caseToOther) *
        caseMultiplicationFactor
      );
    } else if (displayVehicleCapacityValidationErrorMessage) {
      setSnackbarMessage(
        "Some SKU in the selected invoice lack capacity details, so they're excluded in the vehicle capacity calculation."
      );
      setSnackbarOpen(true);
    }
  }
  return 0;
};

const calculateTotalPiecesWeight = (
  initialPieceQuantity: number,
  product: Product,
  caseToPiece: number,
  otherToPiece: number,
  caseMultiplicationFactor: number,
  setSnackbarMessage: Dispatch<SetStateAction<string>>,
  setSnackbarOpen: Dispatch<SetStateAction<boolean>>,
  displayVehicleCapacityValidationErrorMessage: boolean
): number => {
  if (initialPieceQuantity > 0) {
    if (product.skuPieceWeight) {
      return (
        initialPieceQuantity * product.skuPieceWeight * caseMultiplicationFactor
      );
    } else if (product.skuCaseWeight) {
      return (
        initialPieceQuantity *
        (product.skuCaseWeight / caseToPiece) *
        caseMultiplicationFactor
      );
    } else if (product.skuOtherWeight) {
      return (
        initialPieceQuantity *
        (product.skuOtherWeight / otherToPiece) *
        caseMultiplicationFactor
      );
    } else if (displayVehicleCapacityValidationErrorMessage) {
      setSnackbarMessage(
        "Some SKU in the selected invoice lack capacity details, so they're excluded in the vehicle capacity calculation."
      );
      setSnackbarOpen(true);
    }
  }
  return 0;
};

const calculateTotalOtherWeight = (
  initialOtherQuantity: number,
  product: Product,
  caseToOther: number,
  otherToPiece: number,
  caseMultiplicationFactor: number,
  setSnackbarMessage: Dispatch<SetStateAction<string>>,
  setSnackbarOpen: Dispatch<SetStateAction<boolean>>,
  displayVehicleCapacityValidationErrorMessage: boolean
): number => {
  if (initialOtherQuantity && initialOtherQuantity > 0) {
    if (product.skuOtherWeight) {
      return (
        initialOtherQuantity * product.skuOtherWeight * caseMultiplicationFactor
      );
    } else if (product.skuCaseWeight) {
      return (
        initialOtherQuantity *
        (product.skuCaseWeight / caseToOther) *
        caseMultiplicationFactor
      );
    } else if (product.skuPieceWeight) {
      return (
        initialOtherQuantity *
        (product.skuPieceWeight * otherToPiece) *
        caseMultiplicationFactor
      );
    } else if (displayVehicleCapacityValidationErrorMessage) {
      setSnackbarMessage(
        "Some SKU in the selected invoice lack capacity details, so they're excluded in the vehicle capacity calculation."
      );
      setSnackbarOpen(true);
    }
  }
  return 0;
};

const handleVehicleCapacity = (
  product: Product,
  setSnackbarMessage: Dispatch<SetStateAction<string>>,
  setSnackbarOpen: Dispatch<SetStateAction<boolean>>,
  remove: boolean,
  multiplicationFactorForVolume: number,
  totalSkuWeight: number,
  updatedVehicleCapacity: { cubicFeet: number; KG: number },
  displayVehicleCapacityValidationErrorMessage: boolean
) => {
  if (
    !product.skuCaseVolume &&
    (!product.skuCaseBase || !product.skuCaseHeight || !product.skuCaseLength)
  ) {
    if (displayVehicleCapacityValidationErrorMessage) {
      setSnackbarMessage(
        "Some SKU in the selected invoice lack capacity details, so they're excluded in the vehicle capacity calculation."
      );
      setSnackbarOpen(true);
    }
  } else {
    const currentSkuVolume = product.skuCaseVolume
      ? product.skuCaseVolume
      : product.skuCaseBase && product.skuCaseHeight && product.skuCaseLength
      ? product.skuCaseBase *
        product.skuCaseHeight *
        product.skuCaseLength *
        multiplicationFactorForVolume
      : 0;

    updatedVehicleCapacity.cubicFeet += parseFloat(
      truncateToTwoDecimals(remove ? -currentSkuVolume : currentSkuVolume)
    );
    updatedVehicleCapacity.KG += parseFloat(
      truncateToTwoDecimals(remove ? -totalSkuWeight : totalSkuWeight)
    );
  }
};

const populateCart = async (
  data: any,
  updatedProduct: any,
  switchState: boolean,
  dispatch: any,
  products: any,
  totals: any,
  isAdding: boolean,
  setSnackbarMessage: Dispatch<SetStateAction<string>>,
  setSnackbarOpen: Dispatch<SetStateAction<boolean>>,
  performVehiclecalculation: boolean | undefined,
  vehicleUOMKeys: Object,
  updatedVehicleCapacity: { cubicFeet: number; KG: number },
  wasVehicleJustAssigned: boolean,
  displayVehicleCapacityValidationErrorMessage: boolean,
  batchIdConfiguration: boolean,
  loadoutStatus: string,
  previousCheckedIdsSize: number
) => {
  const details = switchState ? data?.salesDetails : data?.orderDetails;
  const isViewLoadout =
    loadoutStatus?.toUpperCase() === "DELIVERED" ||
    loadoutStatus?.toUpperCase() === "IN-TRANSIT";

  details?.forEach((order: any) => {
    if (
      order.caseQuantity >= 0 ||
      order.pieceQuantity >= 0 ||
      order.otherUnitQuantity >= 0
    ) {
      switchState
        ? updateQuantityInvoiceWise(
            order,
            updatedProduct,
            products,
            isAdding,
            totals,
            setSnackbarMessage,
            setSnackbarOpen,
            performVehiclecalculation,
            vehicleUOMKeys,
            updatedVehicleCapacity,
            wasVehicleJustAssigned,
            displayVehicleCapacityValidationErrorMessage,
            batchIdConfiguration,
            isViewLoadout,
            previousCheckedIdsSize
          )
        : updateQuantityOrderWise(
            order,
            updatedProduct,
            switchState,
            products,
            isAdding,
            totals,
            setSnackbarMessage,
            setSnackbarOpen,
            performVehiclecalculation,
            vehicleUOMKeys,
            updatedVehicleCapacity,
            displayVehicleCapacityValidationErrorMessage,
            batchIdConfiguration
          );
    }
  });

  const shouldUpdateTotals = loadoutStatus
    ? true 
    : !wasVehicleJustAssigned || previousCheckedIdsSize === 0; 

  if (isAdding && shouldUpdateTotals) {
    totals.totalAmount += data?.netAmount;
  } else if (!isAdding && shouldUpdateTotals) {
    totals.totalAmount -= data?.netAmount;
  }

  dispatch({ type: "SET_PRODUCT", payload: updatedProduct });
  dispatch({ type: "SET_TOTALS", payload: totals });
};

const fetchLoadoutConfigs = async (domainType: string) => {
  const loadoutConfigs = await getConfigData(
    "loadout_configuration",
    domainType
  );
  return loadoutConfigs[0]?.configValue;
};

const calculateGstAmount = (orderDetails: any) => {
  return orderDetails?.reduce(
    (orderAcc: number, orderDetail: any) =>
      orderAcc +
      (orderDetail.extendedAttributes?.taxInfo?.reduce(
        (taxAcc: number, tax: any) => taxAcc + tax.taxAmount,
        0
      ) || 0),
    0
  );
};

const searchedData = (
  currentData: loadoutsSalesData[],
  switchState: boolean,
  searchVal: string,
  selectedSalesrepOrRoute: string[],
  deliveryStartDate: Dayjs | null,
  deliveryEndDate: Dayjs | null,
  loadoutConfig: ConfigState,
  outletRouteMap: Map<string, string[]>,
  selectedRoute: string[]
) => {
  if (loadoutConfig.presellerRouteMaintained && outletRouteMap.size === 0) {
    return currentData;
  }

  return currentData.filter((eachRow: loadoutsSalesData) => {
    const identifier = switchState
      ? eachRow.invoiceDisplayNumber || eachRow.invoiceNumber
      : eachRow.orderNumber;
    const searchFields = [
      identifier,
      eachRow.creationTime,
      eachRow.outletCode,
      eachRow.outletName,
      eachRow.salesRepId,
      eachRow.salesRepName,
      eachRow.deliveryDate,
    ];

    const matchesSearchVal = searchFields.some((field) =>
      field?.toString().toLowerCase().includes(searchVal.toLowerCase())
    );

    const matchesSalesrepOrRoute = !loadoutConfig.presellerRouteMaintained
      ? selectedSalesrepOrRoute.length
        ? selectedSalesrepOrRoute.some((item) =>
            `${eachRow.salesRepId} - ${eachRow.salesRepName}`
              ?.toLowerCase()
              ?.includes(item.toLowerCase())
          )
        : false
      : selectedSalesrepOrRoute.length
      ? selectedSalesrepOrRoute.some((route) =>
          outletRouteMap.get(route)?.includes(eachRow.outletCode)
        )
      : false;

    const matchesSelectedRoute =
      loadoutConfig.enableDeliveryRouteFilter && selectedRoute.length > 0
        ? selectedRoute.some((routeOption) => {
            const [routeId] = routeOption.split(" - ");
            return eachRow.salesRoute === routeId;
          })
        : true;

    const deliveryDate = dayjs(
      loadoutConfig.dateFilter === "creationTime"
        ? eachRow.creationTime
        : eachRow.deliveryDate
    );
    const isWithinRange =
      (!deliveryStartDate ||
        deliveryDate.isSame(deliveryStartDate, "day") ||
        deliveryDate.isAfter(deliveryStartDate, "day")) &&
      (!deliveryEndDate ||
        deliveryDate.isSame(deliveryEndDate, "day") ||
        deliveryDate.isBefore(deliveryEndDate, "day"));

    return (
      matchesSearchVal &&
      matchesSalesrepOrRoute &&
      matchesSelectedRoute &&
      isWithinRange
    );
  });
};

const extractUniqueOutletCodes = (loadoutData: any): Set<string> => {
  const outletCodes = new Set<string>();
  loadoutData.loadOutDetails?.forEach((detail: any) => {
    if (detail.outletCode) {
      outletCodes.add(detail.outletCode);
    }
  });
  return outletCodes;
};

const getOutletNameMap = async (
  outletCodes: Set<string>
): Promise<Map<string, string[]>> => {
  try {
    if (outletCodes.size === 0) {
      return new Map();
    }
    return await getOutletDetails(Array.from(outletCodes), false);
  } catch (error) {
    await sendSlackAlert(
      SlackChannel.DMS_SECONDARY_UI_NOTIFICATION,
      "Loadout: Failed to fetch outlet details while getOutletNameMap",
      `Loadout: Failed to fetch outlet details while getOutletNameMap while getOutletNameMap in PresellerLoadoutService.tsx`,
      safeStringify({ error: error })
    );
    console.error("Failed to fetch outlet details while getOutletNameMap:", error);
    return new Map();
  }
};

const globalOutletRouteMap = new Map<string, string>();

const getRouteOutletMap = async (
  outletCodes: Set<string>,
  setSnackbarMessage: Dispatch<SetStateAction<string>>,
  setSnackbarOpen: Dispatch<SetStateAction<boolean>>
) => {
  try {
    if (outletCodes.size === 0) {
      return new Map();
    }
    const outletRouteMap = await getOutletDetails(
      Array.from(outletCodes),
      true
    );

    globalOutletRouteMap.clear();
    Array.from(outletRouteMap.entries()).forEach(
      ([routeCode, outletCodeList]) => {
        outletCodeList.forEach((outletCode: string) => {
          globalOutletRouteMap.set(outletCode, routeCode);
        });
      }
    );
    if (globalOutletRouteMap.size !== outletCodes.size) {
      // console.log(
      //   "missedOutlets",
      //   globalOutletRouteMap,
      //   outletCodes,
      //   Array.from(outletCodes).filter(
      //     (outletCode: string) => !globalOutletRouteMap.has(outletCode)
      //   )
      // );
      setSnackbarMessage(
        "Some orders for the selected delivery dates are not currently visible. Please try again later"
      );
      setSnackbarOpen(true);
    }
    return outletRouteMap;
  } catch (error) {
    await sendSlackAlert(
      SlackChannel.DMS_SECONDARY_UI_NOTIFICATION,
      "Loadout: Failed to fetch outlet route map",
      `Loadout: Failed to fetch outlet route map while getRouteOutletMap in PresellerLoadoutService.tsx`,
      safeStringify({ error: error })
    );
    console.error("Failed to fetch outlet route map:", error);
    return new Map();
  }
};

const getOutletDetails = async (
  targetOutletCodes: string[],
  routeWise: boolean
): Promise<Map<string, string[]>> => {
  try {
    if (targetOutletCodes.length === 0) {
      return new Map();
    }
    const outletMap = new Map<string, string[]>();
    const outletData = await getOutletDetailsFromIndexDb();
    const outletDataFiltered = outletData.filter((outlet: any) =>
      targetOutletCodes.includes(outlet.outletCode)
    );

    outletDataFiltered
      .filter(
        (outlet: any) =>
          outlet.outletCode && (routeWise ? outlet.beat : outlet.outletName)
      )
      .forEach((outlet: any) => {
        if (routeWise) {
          // outlet.beat can have multiple beats, comma separated
          if (outlet.beat) {
            const beats = outlet.beat.split(",").map((b: string) => b.trim());
            beats.forEach((beat: string) => {
              const existingOutletCodesForBeat = outletMap.get(beat) || [];
              outletMap.set(beat, [
                ...existingOutletCodesForBeat,
                outlet.outletCode,
              ]);
            });
          }
        } else {
          outletMap.set(outlet.outletCode, [outlet.outletName]);
        }
      });

    return outletMap;
  } catch (error) {
    await sendSlackAlert(
      SlackChannel.DMS_SECONDARY_UI_NOTIFICATION,
      "Loadout: Failed to fetch outlet details while getOutletDetails",
      `Loadout: Failed to fetch outlet details while getOutletDetails while getOutletDetails in PresellerLoadoutService.tsx`,
      safeStringify({ error: error })
    );
    console.error("Failed to fetch outlet details while getOutletDetails:", error);
    throw new Error("Failed to fetch outlet details while getOutletDetails");
  }
};

// Function to create salesRepId to salesRepName map
const getSalesRepNameMap = async (
  allSalesRepOrRoute: string[]
): Promise<Map<string, string>> => {
  try {
    const salesRepMap = new Map<string, string>();
    allSalesRepOrRoute.forEach((salesRep: string) => {
      const [id, name] = salesRep.split(" - ");
      salesRepMap.set(id, name);
    });
    return salesRepMap;
  } catch (error) {
    await sendSlackAlert(
      SlackChannel.DMS_SECONDARY_UI_NOTIFICATION,
      "Loadout: Failed to fetch sales reps while getSalesRepNameMap",
      `Loadout: Failed to fetch sales reps while getSalesRepNameMap while getSalesRepNameMap in PresellerLoadoutService.tsx`,
      safeStringify({ error: error })
    );
    console.error("Failed to fetch sales reps while getSalesRepNameMap:", error);
    return new Map();
  }
};

const getAllSalesMans = async (salesmanDesignations: string[]) => {
  try {
    const loginid = JSON.parse(localStorage.authContext).user.loginId;

    // Make parallel API calls for each designation
    const responses = await Promise.all(
      salesmanDesignations.map(async (designation) => {
        const response = await getActiveUsersBySupplierAndDesignation(
          loginid,
          designation
        );
        return (
          response?.data?.features.map(
            (salesman: any) => `${salesman.loginId} - ${salesman.name}`
          ) || []
        );
      })
    );

    // Combine all results into a single array and remove duplicates
    const combinedData = Array.from(
      new Set(responses.flat()) // Flatten the array of arrays and remove duplicates
    );
    return {
      apiStatus: true,
      data: combinedData,
    };
  } catch (error) {
    await sendSlackAlert(
      SlackChannel.DMS_SECONDARY_UI_NOTIFICATION,
      "Loadout: Failed to fetch salesmen while getAllSalesMans",
      `Loadout: Failed to fetch salesmen while getAllSalesMans while getAllSalesMans in PresellerLoadoutService.tsx`,
      safeStringify({ error: error })
    );
    console.error("Failed to fetch salesmen while getAllSalesMans:", error);
    return {
      apiStatus: false,
      data: [],
    };
  }
};

const getAllSalesReps = async (salesrepDesignations: string[]) => {
  try {
    const loginid = JSON.parse(localStorage.authContext).user.loginId;

    // Make parallel API calls for each designation
    const responses = await Promise.all(
      salesrepDesignations.map(async (designation) => {
        const response = await getActiveUsersBySupplierAndDesignation(
          loginid,
          designation
        );
        return (
          response?.data?.features.map(
            (salesrep: any) => `${salesrep.loginId} - ${salesrep.name}`
          ) || []
        );
      })
    );

    // Combine all results into a single array and remove duplicates
    const combinedData = Array.from(
      new Set(responses.flat()) // Flatten the array of arrays and remove duplicates
    );

    return {
      apiStatus: true,
      data: combinedData,
    };
  } catch (error) {
    await sendSlackAlert(
      SlackChannel.DMS_SECONDARY_UI_NOTIFICATION,
      "Loadout: Failed to fetch sales reps while getAllSalesReps",
      `Loadout: Failed to fetch sales reps while getAllSalesReps while getAllSalesReps in PresellerLoadoutService.tsx`,
      safeStringify({ error: error })
    );
    console.error("Failed to fetch sales reps while getAllSalesReps:", error);
    return {
      apiStatus: false,
      data: [],
    };
  }
};

const deliveryRouteToRouteNameMap = new Map<string, string>();
const getAllDeliveryRoutes = async () => {
  const response = await getDataFromLocalDb("RouteNameIdMapping");
  if (response?.data?.[0]?.data && typeof response.data[0].data === "object") {
    Object.keys(response.data[0].data).forEach((key: string) => {
      const route = response.data[0].data[key];
      if (route.filter1) {
        deliveryRouteToRouteNameMap.set(key, route.filter1);
      }
    });
  }
};

const getAllDeliveryRouteFromRouteInfo = async (
  includeRouteName: boolean = false
) => {
  if (includeRouteName) {
    await getAllDeliveryRoutes();
  }
  const loginid = JSON.parse(localStorage.authContext).user.loginId;
  const response = await getRouteInfoList(loginid);
  const deliveryRouteToSalesmanCodeMap = new Map<string, string[]>();
  response?.data?.features?.forEach((route: any) => {
    if (includeRouteName && deliveryRouteToRouteNameMap.has(route.routeCode)) {
      const existingValues =
        deliveryRouteToSalesmanCodeMap.get(
          `${route.routeCode} - ${deliveryRouteToRouteNameMap.get(
            route.routeCode
          )}`
        ) || [];
      deliveryRouteToSalesmanCodeMap.set(
        `${route.routeCode} - ${deliveryRouteToRouteNameMap.get(
          route.routeCode
        )}`,
        [...existingValues, route.salesmanCode]
      );
    } else {
      deliveryRouteToSalesmanCodeMap.set(route.routeCode, []);
    }
  });
  return {
    apiStatus: true,
    data: Array.from(deliveryRouteToSalesmanCodeMap.keys()),
    deliveryRouteToSalesmanCodeMap,
  };
};

const getAllVehicleData = async (vehicleId: string) => {
  try {
    const response = await getVehicleMaster(vehicleId);
    const vehicles = response?.data?.features.map(
      (vehicle: any): VehicleOption => ({
        id: vehicle.id,
        vehicleId: vehicle.vehicleId,
        vehicleNumber: vehicle.registrationNumber,
        vehicleType: vehicle.vehicleType,
        vehicleCapacityCubicFeet: vehicle?.capacity || 0,
        vehicleCapacityKG: vehicle?.loadCapacity || 0,
      })
    );

    return { apiStatus: true, data: vehicles };
  } catch (error) {
    await sendSlackAlert(
      SlackChannel.DMS_SECONDARY_UI_NOTIFICATION,
      "Loadout: Failed to fetch vehicle data while getAllVehicleData",
      `Loadout: Failed to fetch vehicle data while getAllVehicleData while getAllVehicleData in PresellerLoadoutService.tsx`,
      safeStringify({ error: error })
    );
    console.error("Failed to fetch vehicle data while getAllVehicleData:", error);
    return { apiStatus: false, data: [] };
  }
};

const getOrderAndInvoiceData = async (
  startDate: string,
  endDate: string,
  dataDisplayPreference: DataDisplayPreference[],
  invoiceStatusSet: Set<string>,
  orderStatusSet: Set<string>,
  loadNumber: string | undefined,
  switchState: boolean,
  loadoutStatus: String,
  loadoutConfig: ConfigState,
  loadoutData: any,
  invoiceNumbersSet: Set<string>,
  allSalesRepOrRoute: string[]
): Promise<{
  currCombinedData: any[];
  orderStatus: boolean;
  invoiceStatus: boolean;
  outletCodesSet: Set<string>;
}> => {
  let currCombinedData: any[] = [];
  let orderStatus: boolean = true;
  let invoiceStatus: boolean = true;
  const outletCodesSet = new Set<string>();

  if (loadNumber && loadoutStatus && loadoutStatus !== "IN-DRAFT") {
    try {
      const outletCodes = extractUniqueOutletCodes(loadoutData);
      const [outletNameMap, salesRepNameMap] = await Promise.all([
        getOutletNameMap(outletCodes),
        getSalesRepNameMap(allSalesRepOrRoute),
      ]);

      // Fetch invoice data to get missing fields (creationTime, orderNumber, product MRPs)
      let invoiceData: any[] = [];
      if (loadoutData.loadoutSource === "INVOICE") {
        const invoiceNumbersArray = Array.from(invoiceNumbersSet);
        const invoiceBatches =
          invoiceNumbersSet.size > 100
            ? batchArray(invoiceNumbersArray, 100)
            : [invoiceNumbersArray];

        // Process each batch
        for (const invoiceBatch of invoiceBatches) {
          let page = 0;
          let hasMoreInvoices = true;
          while (hasMoreInvoices) {
            const filters: {
              [key: string]: { op: string; value: string | string[] };
            } = {};

            if (invoiceStatusSet.size > 0) {
              filters["status"] = {
                op: "in",
                value: Array.from(invoiceStatusSet),
              };
            }

            if (invoiceBatch.length > 0) {
              filters["invoiceNumber"] = {
                op: "in",
                value: invoiceBatch,
              };
            }

            const invoiceResponse =
              loadoutConfig?.salesDataApiTemplateVersion === "version2"
                ? await getSalesDataListByDeliveryDate(
                    true,
                    startDate,
                    endDate,
                    loadoutConfig?.dateFilter === "creationTime",
                    page,
                    filters,
                    1000
                  )
                : await getSalesDataByDeliveryDate(
                    true,
                    startDate,
                    endDate,
                    loadoutConfig?.dateFilter === "creationTime",
                    page
                  );

            const features = invoiceResponse?.data?.features || [];
            if (features.length < 1000) {
              hasMoreInvoices = false;
            }
            invoiceData.push(...features);
            page++;
          }
        }
      }

      // Fetch order data if needed (e.g., for orderNumber in some cases)
      let orderData: any[] = [];
      if (loadoutData.loadoutSource === "ORDER") {
        const invoiceNumbersArray = Array.from(invoiceNumbersSet);
        const invoiceBatches =
          invoiceNumbersSet.size > 100
            ? batchArray(invoiceNumbersArray, 100)
            : [invoiceNumbersArray];

        // Process each batch
        for (const invoiceBatch of invoiceBatches) {
          let page = 0;
          let hasMoreOrders = true;
          while (hasMoreOrders) {
            const filters: {
              [key: string]: { op: string; value: string | string[] };
            } = {};
            if (orderStatusSet.size > 0) {
              filters["status"] = {
                op: "in",
                value: Array.from(orderStatusSet),
              };
            }
            if (invoiceBatch.length > 0) {
              filters["orderNumber"] = {
                op: "in",
                value: invoiceBatch,
              };
            }

            const orderResponse = await getOrderListByDeliveryDate(
              startDate,
              endDate,
              "VisitOrder",
              page,
              filters
            );
            const features = orderResponse?.data?.features || [];
            if (features.length < 1000) {
              hasMoreOrders = false;
            }
            orderData.push(...features);
            page++;
          }
        }
      }

      // Create a map for quick lookup of invoice/order data by invoiceNumber/orderNumber
      const invoiceMap = new Map<string, any>();
      invoiceData.forEach((item) => {
        invoiceMap.set(item.invoiceNumber, item);
        outletCodesSet.add(item.outletCode);
      });
      const orderMap = new Map<string, any>();
      orderData.forEach((item) => {
        orderMap.set(item.orderNumber, item);
        outletCodesSet.add(item.outletCode);
      });

      currCombinedData = loadoutData?.loadOutDetails.map((detail: any) => {
        const salesRepName = salesRepNameMap.get(detail?.presellerId) || "";

        // Get corresponding invoice or order data
        const invoice = invoiceMap.get(detail.invoiceNumber);
        const order = orderMap.get(detail.invoiceNumber); // Assuming invoiceNumber might map to orderNumber in some cases

        const invoiceSerialNumber =
          detail.invoiceSerialNumber ||
          invoice?.invoiceSerialNumber;
        const invoiceDisplayNumber =
          invoiceSerialNumber || detail.invoiceNumber;

        // Populate missing fields
        const creationTime =
          invoice?.creationTime ||
          order?.creationTime ||
          loadoutData.loadOutDate ||
          new Date().toISOString();
        const orderNumber = invoice?.orderNumber || detail.invoiceNumber; // Fallback to invoiceNumber if orderNumber is not available
        const salesDetails = detail.loadOutItems.map((item: any) => {
          // Find matching product in invoice.salesDetails or order.orderDetails
          const invoiceProduct =
            invoice?.salesDetails?.find(
              (sale: any) => sale.batchCode === item.batchCode
            ) || {};
          const orderProduct =
            order?.orderDetails?.find(
              (ord: any) => ord.batchCode === item.batchCode
            ) || {};

          return {
            id: item.loadOutDetailsId,
            batchCode: item.batchCode,
            skuCode: item.skuCode,
            initialCaseQuantity: item.caseQty,
            initialPieceQuantity: item.pieceQty,
            initialOtherUnitQuantity: item.otherQty,
            caseQuantity: item.caseQty,
            pieceQuantity: item.pieceQty,
            otherUnitQuantity: item.otherQty,
            netAmount: item.amount,
            billAmount: item.amount,
            // Populate MRP fields from invoice or order
            casePrice:
              invoiceProduct?.casePrice || orderProduct?.casePrice || 0,
            price: invoiceProduct?.price || orderProduct?.price || 0,
            otherUnitPrice:
              invoiceProduct?.otherUnitPrice ||
              orderProduct?.otherUnitPrice ||
              0,
          };
        });

        return {
          id: orderNumber,
          orderNumber, // Populated from invoice data
          invoiceNumber: detail.invoiceNumber,
          invoiceSerialNumber,
          invoiceDisplayNumber,
          creationTime, // Populated from invoice data
          deliveryDate: loadoutData.deliveryStartDate,
          invoiceDate: loadoutData.loadOutDate || new Date().toISOString(),
          outletName:
            outletNameMap.get(detail.outletCode) ||
            invoiceMap.get(detail.invoiceNumber)?.outletName ||
            "",
          outletCode: detail.outletCode,
          salesRepName,
          salesRepId: detail.presellerId,
          salesRoute: detail.routeCode || "",
          salesRouteName: deliveryRouteToRouteNameMap.get(detail.routeCode),
          skuCount: detail.loadOutItems?.length || 0,
          totalCaseQuantity: detail?.totalCaseQty || 0,
          totalPieceQuantity: detail?.totalPieceQty || 0,
          totalOtherUnitQuantity: detail?.totalOtherQty || 0,
          netAmount: detail?.salesInfo?.finalAmount || 0,
          billAmount: detail.totalAmount || 0,
          gstAmount: detail?.salesInfo?.gstAmount || 0,
          type: loadoutData.loadoutSource,
          status: invoice?.status, // to be done once changed in creatdloadoutservice : detail?.salesInfo?.invoiceStatus
          discount: detail?.salesInfo?.discountAmount,
          totalDiscountValue: detail?.salesInfo?.discountAmount,
          discountInfo: [],
          salesDetails, // Updated with MRP details
          showEyeIcon: true,
          downloadId: detail.invoiceNumber,
        };
      });

      invoiceStatus = true;
    } catch (err) {
      await sendSlackAlert(
        SlackChannel.DMS_SECONDARY_UI_NOTIFICATION,
        "Loadout: Failed to fetch loadout order and invoice data while getOrderAndInvoiceData",
        `Loadout: Failed to fetch loadout order and invoice data while getOrderAndInvoiceData while getOrderAndInvoiceData in PresellerLoadoutService.tsx`,
        safeStringify({ error: err })
      );
      console.error("Failed to fetch loadout order and invoice data while getOrderAndInvoiceData:", err);
      invoiceStatus = false;
    } finally {
      return {
        currCombinedData,
        orderStatus,
        invoiceStatus,
        outletCodesSet,
      };
    }
  } else {
    // Existing logic for IN-DRAFT or no loadNumber (unchanged)
    let orderData: any[] = [];
    let invoiceData: any[] = [];

    const shouldFetchOrders =
      (loadNumber && !switchState) ||
      (!loadNumber &&
        dataDisplayPreference?.some(
          (preference) => preference.type === "order" && preference.visible
        ));

    const shouldFetchInvoices =
      (loadNumber && switchState) ||
      (!loadNumber &&
        dataDisplayPreference?.some(
          (preference) => preference.type === "invoice" && preference.visible
        ));

    const fetchOrders = async () => {
      if (!shouldFetchOrders) return;

      let page = 0;
      let hasMoreOrders = true;

      try {
        while (hasMoreOrders) {
          const filters: {
            [key: string]: { op: string; value: string | string[] };
          } = {};

          if (orderStatusSet.size > 0) {
            filters["status"] = {
              op: "in",
              value: Array.from(orderStatusSet),
            };
          }

          const orderResponse = await getOrderListByDeliveryDate(
            startDate,
            endDate,
            "VisitOrder",
            page,
            filters
          );

          const features = orderResponse?.data?.features || [];

          if (features.length < 1000) {
            hasMoreOrders = false;
          }

          const newOrderData = features.map((item: loadoutsSalesData) => ({
            ...item,
            salesRoute: item?.beat,
            salesRouteName: deliveryRouteToRouteNameMap.get(item?.beat),
            skuCount: item.orderDetails?.length,
            gstAmount: calculateGstAmount(item.orderDetails),
            type: "ORDER",
            showEyeIcon: true,
            invoiceDisplayNumber:
              item?.invoiceSerialNumber || item?.invoiceNumber,
          }));

          newOrderData.forEach((item: loadoutsSalesData) => {
            if (item.outletCode) {
              outletCodesSet.add(item.outletCode);
            }
          });
          const outletNameMap = await getOutletNameMap(outletCodesSet);
          newOrderData.forEach((item: loadoutsSalesData) => {
            item.outletName = outletNameMap.get(item.outletCode)?.[0] || "";
          });

          orderData.push(...newOrderData);

          page++;
        }
      } catch (err) {
        await sendSlackAlert(
          SlackChannel.DMS_SECONDARY_UI_NOTIFICATION,
          "Loadout: Failed to fetch order data while fetchOrders",
          `Loadout: Failed to fetch order data while fetchOrders while fetchOrders in PresellerLoadoutService.tsx`,
          safeStringify({ error: err })
        );
        console.error("Failed to fetch order data while fetchOrders:", err);
        orderStatus = false;
      }
    };

    const fetchInvoices = async () => {
      if (!shouldFetchInvoices) return;

      let page = 0;
      let hasMoreInvoices = true;

      while (hasMoreInvoices) {
        try {
          const filters: {
            [key: string]: { op: string; value: string | string[] };
          } = {};

          if (invoiceStatusSet.size > 0) {
            filters["status"] = {
              op: "in",
              value: Array.from(invoiceStatusSet),
            };
          }

          const invoiceResponse =
            loadoutConfig?.salesDataApiTemplateVersion === "version2"
              ? await getSalesDataListByDeliveryDate(
                  true,
                  startDate,
                  endDate,
                  loadoutConfig?.dateFilter === "creationTime",
                  page,
                  filters,
                  1000
                )
              : await getSalesDataByDeliveryDate(
                  true,
                  startDate,
                  endDate,
                  loadoutConfig?.dateFilter === "creationTime",
                  page
                );
          let features = invoiceResponse?.data?.features || [];


          const dayEndObj = getDayEndStatusFromSession();

          if (dayEndObj && dayEndObj.isDayEndInProgress && loadoutConfig.dateFilter === 'creationTime') {
            features = features.filter((invoice: any) => {
              const orderCreationTime = dayjs(invoice.extendedAttributes?.orderCreationTime);

              const startDate = dayjs(dayEndObj.startDate);
              const endDate = dayjs(dayEndObj.endDate);

              return orderCreationTime.isAfter(startDate) && orderCreationTime.isBefore(endDate);
              //
              //
              // const validInvoices = dayEndObj.invoices
              // return validInvoices.includes(invoice.orderNumber);
            });
          }

          if (features.length < 1000) {
            hasMoreInvoices = false;
          }

          const newInvoiceData = features.map((item: loadoutsSalesData) => ({
            ...item,
            id: item.orderNumber,
            skuCount: item.salesDetails?.length,
            gstAmount: calculateGstAmount(item.salesDetails),
            type: "INVOICE",
            showEyeIcon: true,
            downloadId: item.invoiceNumber,
            salesRoute: item?.beat,
            salesRouteName: deliveryRouteToRouteNameMap.get(item?.beat),
            invoiceDisplayNumber:
              item?.invoiceSerialNumber || item?.invoiceNumber,
          }));

          newInvoiceData.forEach((item: loadoutsSalesData) => {
            if (item.outletCode) {
              outletCodesSet.add(item.outletCode);
            }
          });
          const outletNameMap = await getOutletNameMap(outletCodesSet);
          newInvoiceData.forEach((item: loadoutsSalesData) => {
            item.outletName = outletNameMap.get(item.outletCode)?.[0] || "";
          });

          invoiceData.push(...newInvoiceData);

          page++;
        } catch (err) {
          await sendSlackAlert(
            SlackChannel.DMS_SECONDARY_UI_NOTIFICATION,
            "Loadout: Failed to fetch invoice data while fetchInvoices",
            `Loadout: Failed to fetch invoice data while fetchInvoices while fetchInvoices in PresellerLoadoutService.tsx`,
            safeStringify({ error: err })
          );
          console.error("Failed to fetch invoice data while fetchInvoices:", err);
          invoiceStatus = false;
          hasMoreInvoices = false;
        }
      }
    };

    await Promise.all([fetchOrders(), fetchInvoices()]);
    currCombinedData = [...orderData, ...invoiceData];
  }

  return {
    currCombinedData,
    orderStatus,
    invoiceStatus,
    outletCodesSet,
  };
};

function debounce<T extends (...args: any[]) => void>(
  func: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timer: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timer); // Clear previous timer
    timer = setTimeout(() => func(...args), delay);
  };
}

const debouncedPopup = debounce(
  (
    invoiceNumbersSet: Set<string>,
    filteredData: loadoutsSalesData[],
    switchState: boolean,
    loadoutStatus: string,
    setInvoicesCount: Dispatch<SetStateAction<InvoicesCount>>
  ) => {
    if (filteredData.length === 0) return;

    const invalidItems: string[] = [];

    invoiceNumbersSet.forEach((invoiceNumber) => {
      const found = filteredData.some((item) =>
        switchState
          ? item.invoiceNumber === invoiceNumber
          : item.orderNumber === invoiceNumber
      );

      if (!found && loadoutStatus === "IN-DRAFT") {
        invalidItems.push(invoiceNumber);
      }
    });

    setInvoicesCount((prev) => ({
      valid: prev.valid,
      invalid: invalidItems,
    }));
  },
  1000
);

const getDataBasedOnToggle = (
  switchState: boolean,
  dataToUpdate: loadoutsSalesData[],
  invoiceStatusSet: Set<string>,
  loadoutStatus: string,
  invoiceNumbersSet: Set<string>,
  invoiceActions: ActionDisplayPreference | undefined,
  orderActions: ActionDisplayPreference | undefined,
  setInvoicesCount: Dispatch<SetStateAction<InvoicesCount>>,
  columnsMetaData: any
) => {
  const loginid = JSON.parse(localStorage.authContext).user.loginId;
  const updatedColumnsMetaData: any = { ...columnsMetaData };

  updatedColumnsMetaData.Actions = [];
  if (invoiceActions?.DisplayEyeIcon && switchState)
    updatedColumnsMetaData.Actions.push("view");
  if (invoiceActions?.DisplayDownloadIcon && switchState)
    updatedColumnsMetaData.Actions.push("download");

  if (
    orderActions?.DisplayEyeIcon &&
    !updatedColumnsMetaData.Actions.includes("view") &&
    !switchState
  )
    updatedColumnsMetaData.Actions.push("view");
  if (
    orderActions?.DisplayDownloadIcon &&
    !updatedColumnsMetaData.Actions.includes("download") &&
    !switchState
  )
    updatedColumnsMetaData.Actions.push("download");

  const targetIndex = updatedColumnsMetaData.columns.findIndex(
    (col: any) =>
      col.headerName === "Order No. & Date" ||
      col.headerName === "Invoice No. & Date"
  );

  if (targetIndex !== -1) {
    updatedColumnsMetaData.columns[targetIndex].headerName = switchState
      ? "Invoice No. & Date"
      : "Order No. & Date";

    updatedColumnsMetaData.columns[targetIndex].mappedValue = switchState
      ? "invoiceDisplayNumber, creationTime"
      : "orderNumber, creationTime";
  }

  const filteredData = dataToUpdate.filter((item: any) => {
    const isInvoice =
      switchState &&
      item.type === "INVOICE" &&
      invoiceStatusSet.has(item.status);
    const isOrder = !switchState && item.type === "ORDER";
    const isInInvoiceSet =
      invoiceNumbersSet.size > 0 && item.type === "INVOICE"
        ? invoiceNumbersSet.has(item.invoiceNumber)
        : invoiceNumbersSet.has(item.orderNumber);

    return (
      (isInvoice || isOrder) &&
      (!loadoutStatus || loadoutStatus === "IN-DRAFT" || isInInvoiceSet) &&
      item.outletCode !== loginid // This check makes sure Primary Orders are not included
    );
  });

  // Debounced popup logic
  debouncedPopup(
    invoiceNumbersSet,
    filteredData,
    switchState,
    loadoutStatus,
    setInvoicesCount
  );

  return { updatedColumnsMetaData, filteredData };
};

const onInvoiceDownLoad = async (
  invoiceNumber: string,
  setSnackbarOpen: Dispatch<SetStateAction<boolean>>
) => {
  try {
    setSnackbarOpen(true);
    const ACCESS_TOKEN = getAccessToken() ?? "";
    const blobKey = await createAndDownloadInvoicePdf([invoiceNumber]);
    if (!blobKey || !ACCESS_TOKEN) {
      console.error("Invalid URL or access token.");
      return;
    }
    const pdfUrl = `${blobKey}?access_token=${ACCESS_TOKEN}`;
    window.open(pdfUrl, "_blank");
  } catch (e) {
    await sendSlackAlert(
      SlackChannel.DMS_SECONDARY_UI_NOTIFICATION,
      "Loadout: Failed to download invoice while onInvoiceDownLoad",
      `Loadout: Failed to download invoice while onInvoiceDownLoad while onInvoiceDownLoad in PresellerLoadoutService.tsx`,
      safeStringify({ error: e })
    );
    console.error("some error occured", e);
    openPopup("Error", "Some error occured while downloading invoice");
  } finally {
    setSnackbarOpen(false);
  }
};

const getAllowedStatuses = (
  loadoutStatus: String | null,
  allowedStatuses: AllowedStatuses
) => {
  const currentlyAllowedStatuses = new Set<string>();

  if (loadoutStatus === "DELIVERED") {
    allowedStatuses.deliveredLoadout.forEach((status) =>
      currentlyAllowedStatuses.add(status)
    );
  } else if (loadoutStatus === "IN-TRANSIT") {
    allowedStatuses.inTransitLoadout.forEach((status) =>
      currentlyAllowedStatuses.add(status)
    );
  } else {
    allowedStatuses.createLoadout.forEach((status) =>
      currentlyAllowedStatuses.add(status)
    );
  }

  return currentlyAllowedStatuses;
};

const fetchOrderConfigs = async (domainType: string): Promise<number> => {
  const orderConfigs = await getConfigData("order_configuration", domainType);
  return orderConfigs[0]?.configValue;
};

const fetchDmsConfig = async (domainType: string): Promise<boolean> => {
  const dmsConfig = await getConfigData("dms_configuration", domainType);
  return dmsConfig[0]?.configValue;
};

const defaultAllowedStatuses = {
  createLoadout: ["INVOICED", "PARTIALLY INVOICED"],
  inTransitLoadout: [
    "OUT FOR DELIVERY",
    "DELIVERED",
    "CANCELLED",
    "PARTIAL DELIVERED",
  ],
  deliveredLoadout: ["DELIVERED", "CANCELLED", "PARTIAL DELIVERED"],
};

const setLoadoutConfig = async (
  loadoutConfigValues: any,
  dispatch: Dispatch<AnyAction>
) => {
  // Fetch the configuration values asynchronously
  const [maxOrderDeliveryDateOffsetValue, batchIdConfigurationValue] =
    await Promise.all([
      fetchOrderConfigs("orderFulfillmentTime"),
      fetchDmsConfig("batchIdConfiguration"),
    ]);

  const fetchedLoadoutConfig: ConfigState = {
    displayPresellerSummary: loadoutConfigValues?.Preseller?.DisplayLoadSummary,
    displayRsuSummary: loadoutConfigValues?.Rsu?.DisplayLoadSummary,
    presellerRouteMaintained: loadoutConfigValues?.Preseller?.RouteMaintained,
    rsuRouteMaintained: loadoutConfigValues?.Rsu?.RouteMaintained,
    summaryKeys: loadoutConfigValues?.Preseller?.SummaryKeys,
    maxDeliveryDateOffset: loadoutConfigValues?.Preseller?.DateFilter === "creationTime"
      ? 0
      : maxOrderDeliveryDateOffsetValue ?? 2,
    gapBetweenCreationAndDeliveryDate: maxOrderDeliveryDateOffsetValue ?? 0,
    minDeliveryDateOffset:
      loadoutConfigValues?.Preseller?.MinDeliveryDateOffset ?? 15,
    maxInvoiceSelectionLimit:
      loadoutConfigValues?.Preseller?.MaxInvoiceSelectionLimit ?? 50,
    dataDisplayPreference:
      loadoutConfigValues?.Preseller?.DataDisplayPreference,
    enableSalesmanAssignment:
      loadoutConfigValues?.Preseller?.EnableSalesmanAssignment,
    enableVehicleAssignment:
      loadoutConfigValues?.Preseller?.EnableVehicleAssignment,
    enableDeliveryDateAssignment:
      loadoutConfigValues?.Preseller?.EnableDeliveryDateAssignment,
    dateFilter: loadoutConfigValues?.Preseller?.DateFilter ?? "deliveryDate",
    enableStgExcelLoadoutSheetDownload:
      loadoutConfigValues?.Preseller?.EnableStgExcelLoadoutSheetDownload ??
      false,
    allowedStatuses:
      loadoutConfigValues?.Preseller?.AllowedStatuses ?? defaultAllowedStatuses,
    isStockServiceEnabled:
      loadoutConfigValues?.Preseller?.IsStockServiceEnabled ?? false,
    invoiceActions: loadoutConfigValues?.Preseller?.InvoiceActions,
    orderActions: loadoutConfigValues?.Preseller?.OrderActions,
    loadoutActions: loadoutConfigValues?.Preseller?.LoadoutActions,
    vehicleUOMKeys: loadoutConfigValues?.Preseller?.VehicleUOMKeys,
    displayVehicleCapacityValidationErrorMessage:
      loadoutConfigValues?.Preseller
        ?.DisplayVehicleCapacityValidationErrorMessage ?? false,
    loadslipColumnLabel: loadoutConfigValues?.Preseller?.LoadslipColumnLabel,
    loadslipGroupByField: loadoutConfigValues?.Preseller?.LoadslipGroupByField,
    salesDataApiTemplateVersion:
      loadoutConfigValues?.Preseller?.SalesDataApiTemplateVersion ?? "version1",
    loadslipMultipleFilters:
      loadoutConfigValues?.Preseller?.LoadslipMultipleFilters,
    loadSlipConfigs: loadoutConfigValues?.Preseller?.LoadSlipConfigs,
    enableDeliveryRouteFilter:
      loadoutConfigValues?.Preseller?.EnableDeliveryRouteFilter ?? false,
    enableVehicleCapacityValidation:
      loadoutConfigValues?.Preseller?.EnableVehicleCapacityValidation,
    salesmanDesignations:
      loadoutConfigValues?.Preseller?.SalesmanDesignations ?? [],
    salesrepDesignations:
      loadoutConfigValues?.Preseller?.SalesrepDesignations ?? [],
    filtersConfig:
      loadoutConfigValues?.Preseller?.FiltersConfig || initalFiltersConfig,
    presellerColumnsConfig:
      loadoutConfigValues?.Preseller?.PresellerColumnsConfig ||
      presellerColumnsMetaData,
    loadoutColumnsConfig:
      loadoutConfigValues?.Preseller?.LoadoutColumnsConfig ||
      loadOutColumnsMetaData,
    batchIdConfiguration: batchIdConfigurationValue ?? false,
  };

  dispatch({ type: "SET_LOADOUT_CONFIG", payload: fetchedLoadoutConfig });
};

const printLoadslip = async (
  loadNumber: string,
  cart: any,
  setSnackbarOpen: Dispatch<SetStateAction<boolean>>,
  setIsDownloading: Dispatch<SetStateAction<boolean>>,
  isDownloading: boolean,
  openPopUp: (message: string, type: popupType) => void,
  setSnackbarMessage: Dispatch<SetStateAction<string>>,
  loadSlipConfigs: any,
  selectedOptions: string[]
) => {
  const printLoadsheet = async () => {
    if (isDownloading) return;
    try {
      setIsDownloading(true);
      if (cart?.loadoutConfig?.enableStgExcelLoadoutSheetDownload) {
        setSnackbarMessage("Your loadslip report will be downloaded shortly.");
        const timestamp = new Date()
          .toISOString()
          .slice(0, 19)
          .replace("T", " ")
          .replace(/:/g, "-");
        await getLoadoutExcelSheet(
          loadNumber,
          "dms_loading_sheet",
          "Loading Sheet " + timestamp + ".csv"
        );
        await getLoadoutExcelSheet(
          loadNumber,
          "dms_loadout_items",
          "Loading Sheet Product Wise " + timestamp + ".csv"
        );
      } else {
        const loadoutReportIdConfig = await getConfigData(
          "loadout_configuration",
          "loadoutReportId"
        );

        const filters = [{ key: "loadNumber", value: loadNumber ?? "" }];

        if (
          cart?.loadoutConfig?.loadslipGroupByField ||
          (selectedOptions.includes("mrp") && loadSlipConfigs?.groupByField)
        ) {
          filters.push({
            key: "groupByField",
            value:
              cart?.loadoutConfig?.loadslipGroupByField ??
              loadSlipConfigs?.groupByField ??
              "",
          });

          if (
            cart?.loadoutConfig?.loadslipColumnLabel ||
            loadSlipConfigs?.columnLabel
          ) {
            filters.push({
              key: "columnLabel",
              value:
                cart?.loadoutConfig?.loadslipColumnLabel ??
                loadSlipConfigs?.columnLabel ??
                "",
            });
          }
        }

        if (loadSlipConfigs?.showInvoiceDetails) {
          filters.push({
            key: "showInvoiceDetails",
            value: loadSlipConfigs?.showInvoiceDetails ?? true,
          });
        }

        if (selectedOptions.includes("skucode")) {
          filters.push({
            key: "showSkuSummary",
            value: "true",
          });
        }

        const downloadSingleReportConfig = getLoadoutDownloadSingleReportConfig(
          loadoutReportIdConfig[0]?.configValue ?? "",
          filters
        );

        const resolvedConfig: DownloadSingleReportConfig = {
          ...downloadSingleReportConfig,
          filters: downloadSingleReportConfig.filters,
        };

        setSnackbarMessage("Your loadslip will be downloaded shortly.");
        await downloadSingleReport(
          resolvedConfig,
          setSnackbarOpen,
          setIsDownloading
        );
      }
    } catch (e) {
      await sendSlackAlert(
        SlackChannel.DMS_SECONDARY_UI_NOTIFICATION,
        "Loadout: Failed to download loadslip while printLoadslip",
        `Loadout: Failed to download loadslip while printLoadslip while printLoadslip in PresellerLoadoutService.tsx`,
        safeStringify({ error: e })
      );
      openPopUp("Something went wrong while downloading loadslip", "Error");
      console.error(encodeURI);
    } finally {
      setIsDownloading(false);
    }
  };

  logLoadTrace(printLoadsheet, "delivery_loadout_print_loadslip_time");
};

export {
  populateCart,
  fetchLoadoutConfigs,
  calculateGstAmount,
  searchedData,
  getAllSalesMans,
  getAllSalesReps,
  getAllDeliveryRouteFromRouteInfo,
  deliveryRouteToRouteNameMap,
  getAllVehicleData,
  getOrderAndInvoiceData,
  getDataBasedOnToggle,
  onInvoiceDownLoad,
  getAllowedStatuses,
  setLoadoutConfig,
  getRouteOutletMap,
  globalOutletRouteMap,
  printLoadslip,
};
